import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

import type { HomepageContextEntity } from "@superblocksteam/library-shared";
import type { AiMode, Attachment } from "@superblocksteam/library-shared/types";
import type {
  IHomepageApplicationV2Dto,
  PostApplicationCreateRequestBody,
} from "@superblocksteam/shared";

import { startEvaluation } from "legacy/actions/evaluationActions";

import { updateApplicationMetadata } from "../application/applicationActions";

interface HomepageState {
  applications: IHomepageApplicationV2Dto[];
  isCreatingApplication: boolean;
  lastCreatedApplicationId?: string;
  isDuplicatingApplication: boolean;
}

const initialState: HomepageState = {
  applications: [],
  isCreatingApplication: false,
  lastCreatedApplicationId: undefined,
  isDuplicatingApplication: false,
};

// Create and duplicate run through createApplicationSaga and
// duplicateApplicationSaga. The rest of the home page uses reduxApi.
export const homepageSlice = createSlice({
  name: "homepage",
  initialState,
  reducers: {
    createApplicationInit(
      state,
      // This type is used by the ApplicationSagas.ts
      action: PayloadAction<{
        applicationName: string;
        orgId: string;
        createUsingReact: boolean;
        folderId?: string;
        templateName?: string;
        navigateOpts?: {
          replace?: boolean;
        };
        initialPrompt?: string;
        initialAttachments?: Attachment[];
        initialContextEntities?: HomepageContextEntity[];
        initialImportSource?: string;
        initialMode?: AiMode;
        builderHarness?: PostApplicationCreateRequestBody["builderHarness"];
        builderModel?: PostApplicationCreateRequestBody["builderModel"];
      }>,
    ) {
      state.isCreatingApplication = true;
    },
    createApplicationSuccess(
      state,
      action: PayloadAction<IHomepageApplicationV2Dto>,
    ) {
      state.isCreatingApplication = false;
      state.lastCreatedApplicationId = action.payload.id;
      state.applications.push(action.payload);
    },
    createApplicationError(state) {
      state.isCreatingApplication = false;
    },
    duplicateApplicationInit(
      state,
      action: PayloadAction<{ applicationId: string }>,
    ) {
      state.isDuplicatingApplication = true;
    },
    duplicateApplicationSuccess(state) {
      state.isDuplicatingApplication = false;
    },
    duplicateApplicationError(state) {
      state.isDuplicatingApplication = false;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(startEvaluation, () => initialState)
      .addCase(updateApplicationMetadata, (state, action) => {
        state.applications.forEach((app) => {
          if (app.id !== action.payload.id) return;
          // immediately update name and folder
          if (action.payload.name) app.name = action.payload.name;
          if (action.payload.folderId !== undefined)
            app.folderId = action.payload.folderId;
        });
      }),
});

export const {
  createApplicationInit,
  createApplicationSuccess,
  createApplicationError,
  duplicateApplicationInit,
  duplicateApplicationSuccess,
  duplicateApplicationError,
} = homepageSlice.actions;
